import { createFileRoute, Link, notFound, Outlet, useRouterState } from '@tanstack/react-router'
import { meQuery } from '@/lib/queries'
import { cn } from '@/lib/utils'

/**
 * The deployment's own view of itself.
 *
 * A layout rather than a page because there is more than one operational
 * question and they do not belong on one screen: what we are spending, whether
 * the worker is keeping up, how stale each site's data is, whether the checks
 * we ship are still running and still worth shipping, and what we are asking
 * models.
 *
 * Everything here is what the customer-facing reports deliberately withhold —
 * which supplier a number came from, how often we go and get it, what it cost.
 * That is the trade: the reports stay about the site, and this is where the
 * plumbing is legible.
 */
export const Route = createFileRoute('/_app/admin')({
  /**
   * A 404, not a redirect. This area is not something a customer should learn
   * exists by being bounced out of it, and the api answers the same way for the
   * same reason — this check only saves the round trip.
   */
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery)
    if (!me.isStaff) throw notFound()
  },
  component: AdminLayout,
})

const TABS = [
  { label: 'Costs', to: '/admin', leaf: '' },
  { label: 'Queues', to: '/admin/queues', leaf: 'queues' },
  { label: 'Feeds', to: '/admin/feeds', leaf: 'feeds' },
  { label: 'Actions', to: '/admin/actions', leaf: 'actions' },
  { label: 'AI', to: '/admin/ai', leaf: 'ai' },
] as const

function AdminLayout() {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const leaf = pathname.split('/').filter(Boolean)[1] ?? ''

  return (
    <>
      {/* Its own tab row rather than the workspace one: this is not scoped to a
          tenant, and putting it in the header would put a door to it in front of
          every customer. */}
      <nav className="mb-5 flex items-center gap-1 border-b border-border">
        {TABS.map((tab) => (
          <Link
            key={tab.to}
            to={tab.to}
            className={cn(
              'mono-label -mb-px border-b-2 px-3 py-2 transition-colors',
              leaf === tab.leaf
                ? 'border-plum text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {tab.label}
          </Link>
        ))}
      </nav>

      <Outlet />
    </>
  )
}
